import React from "react";
import Seo from "../components/Seo";
import { Section, Eyebrow, PageHero } from "../components/primitives";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTABanner from "../components/CTABanner";
import { Bluetooth, RefreshCw, Layers, Network } from "lucide-react";
import { DEVICE_CATEGORIES } from "../lib/content";

export default function SmartScale() {
  return (
    <div>
      <Seo
        title="Smart Scale"
        path="/smart-scale"
        noindex
        description="Jollof Fit Kingdom is designed to support connected wellness technology — Bluetooth integration, body composition and automatic synchronization for a connected health ecosystem."
      />
      <PageHero
        eyebrow="Smart Scale"
        title="Your body composition,"
        highlight="automatically synced"
        description="Jollof Fit Kingdom is designed to support compatible connected wellness devices — bringing weight, body fat, muscle and hydration directly into your connected fitness ecosystem."
      />

      <Section className="mt-24">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>Connected by design</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
              A seamless bridge to your body's data
            </h2>
          </Reveal>
        </div>
        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Bluetooth, t: "Bluetooth integration", d: "Direct, secure connections to compatible smart scales for effortless sync." },
            { icon: Layers, t: "Body composition", d: "Weight, body fat, muscle mass and hydration captured in rich detail." },
            { icon: RefreshCw, t: "Automatic sync", d: "Step on the scale — your metrics appear in Jollof Fit Kingdom instantly." },
            { icon: Network, t: "Future ecosystem", d: "A growing network of connected devices working together seamlessly." },
          ].map((f) => (
            <StaggerItem key={f.t}>
              <div className="h-full rounded-2xl border border-white/10 bg-[#0a0a0a] p-7 transition-colors duration-300 hover:border-gold/30">
                <f.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-lg text-white">{f.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-zinc-400">{f.d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* Supported device categories */}
      <Section className="mt-28">
        <Reveal>
          <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 md:p-12">
            <Eyebrow>Future Compatibility</Eyebrow>
            <h2 className="mt-4 font-heading text-2xl font-medium tracking-tight text-white md:text-3xl">
              Designed to support connected wellness technology
            </h2>
            <p className="mt-4 max-w-2xl text-sm text-zinc-400">
              Jollof Fit Kingdom is built to work with supported smart health devices across the categories below. You
              choose which supported device or service to connect, data is received only when you authorize the
              connection, compatibility depends on the supported platform or device, and you can disconnect any
              integration at any time. Are you a device maker interested in connected wellness? We'd love to talk.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {DEVICE_CATEGORIES.map((m) => (
                <span
                  key={m}
                  data-testid={`device-category-${m.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-5 py-2.5 font-heading text-base text-zinc-300 transition-colors duration-300 hover:border-gold/40 hover:text-white"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>

      <CTABanner
        title="Building connected wellness devices?"
        desc="Let's explore Bluetooth compatibility and bring your hardware into the Jollof Fit Kingdom ecosystem."
      />
    </div>
  );
}
